import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  dialogSnapshot,
  settleDialog,
  subscribeDialogs,
  type DialogRequest,
} from "../dialog";

/* Renders the confirmation queue. Only the first is shown: these are decisions,
   and stacking two of them over each other is how the wrong one gets answered. */

function Dialog({ request }: { request: DialogRequest }) {
  const [typed, setTyped] = useState(request.prompt?.initial ?? "");
  const panel = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);

  const armed = request.prompt
    ? typed.trim().length > 0
    : !request.typeToConfirm || typed === request.typeToConfirm;

  /* A prompt answers with what was typed; everything else answers yes. */
  const accept = () => settleDialog(request.id, request.prompt ? typed.trim() : true);

  /* Where focus lands says which answer is the safe one. With nothing to type,
     that is Cancel — a stray Enter on a "delete everything" prompt should not
     be the thing that deletes everything. When there is a phrase to type, the
     confirm button is already gated behind it, so the field is both safe and
     the obvious place to start. */
  useEffect(() => {
    const target = field.current ?? cancel.current;
    target?.focus();
    // A prompt arrives with the old name in it, ready to be typed over.
    if (field.current && request.prompt) field.current.select();
  }, [request.prompt]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        settleDialog(request.id, false);
        return;
      }
      if (event.key !== "Tab") return;
      // A modal that lets focus wander behind it is a modal in appearance only:
      // the page under this is still there, and its buttons still work.
      const focusable = panel.current?.querySelectorAll<HTMLElement>(
        "button:not(:disabled), input, [href], select, textarea",
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [request.id]);

  return (
    <div
      className="dialog-scrim"
      onMouseDown={(event) => {
        // Only a press that both starts and ends on the backdrop dismisses, so
        // a text selection that drags out of the panel does not close it.
        if (event.target === event.currentTarget) settleDialog(request.id, false);
      }}
    >
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`dialog-title-${request.id}`}
        ref={panel}
      >
        <h2 id={`dialog-title-${request.id}`}>{request.title}</h2>
        <p>{request.body}</p>

        {(request.typeToConfirm || request.prompt) && (
          <label className="dialog-confirm-field">
            {request.prompt ? (
              request.prompt.label
            ) : (
              <>
                Type <code>{request.typeToConfirm}</code> to confirm
              </>
            )}
            <input
              type="text"
              value={typed}
              ref={field}
              autoComplete="off"
              spellCheck={false}
              placeholder={request.prompt?.placeholder}
              maxLength={request.prompt?.maxLength}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && armed) accept();
              }}
            />
          </label>
        )}

        <div className="dialog-actions">
          <button
            className="ghost"
            ref={cancel}
            onClick={() => settleDialog(request.id, false)}
          >
            Cancel
          </button>
          <button
            className={request.danger ? "danger-solid" : ""}
            disabled={!armed}
            onClick={accept}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Dialogs() {
  const queue = useSyncExternalStore(subscribeDialogs, dialogSnapshot);
  const current = queue[0];
  // Keyed so a second confirmation starts with an empty type-to-confirm box
  // rather than inheriting the previous one's.
  return current ? <Dialog key={current.id} request={current} /> : null;
}
